import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/app/StoreContext';
import { fx } from '@/platform/feedback';
import styles from './Timer.module.css';

const R = 16;
const C = 2 * Math.PI * R; // circumference ≈ 100.53

export const Timer = observer(function Timer() {
  const { round } = useStore();
  const [, setTick] = useState(0);
  const lastTickSecRef = useRef<number>(-1);

  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 250);
    return () => clearInterval(id);
  }, []);

  const now = Date.now() + round.serverClockOffsetMs;
  const endsAt = round.endsAtUtc ? new Date(round.endsAtUtc).getTime() : now;
  const startedAt = round.startedAtUtc ? new Date(round.startedAtUtc).getTime() : now;
  const totalMs = Math.max(1, endsAt - startedAt);
  const ms = Math.max(0, endsAt - now);
  const pct = ms / totalMs;

  const totalSec = Math.ceil(ms / 1000);
  const mm = String(Math.floor(totalSec / 60));
  const ss = String(totalSec % 60).padStart(2, '0');
  const urgent = ms < 10_000;

  // tick sound on each second change in urgent zone (1–9s remaining)
  if (urgent && totalSec > 0 && totalSec !== lastTickSecRef.current) {
    lastTickSecRef.current = totalSec;
    fx.tick();
  }

  const dashoffset = C * (1 - pct);

  return (
    <div className={`${styles.timer} ${urgent ? styles.urgent : ''}`} aria-hidden="true">
      <svg className={styles.ring} width="36" height="36" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r={R} fill="none" className={styles.ringTrack} strokeWidth="3" />
        <circle
          cx="18" cy="18" r={R} fill="none"
          className={styles.ringProgress}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={dashoffset}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className={styles.digits}>{mm}:{ss}</span>
    </div>
  );
});
